import * as React from "react";
import { TabsContent } from "@/taskpane/components/ui/tabs";
import { Alert, AlertDescription } from "@/taskpane/components/ui/alert";
import { Button } from "@/taskpane/components/ui/button";
import { Input } from "@/taskpane/components/ui/input";
import { Field, FieldContent } from "@/taskpane/components/ui/field";
import { Info, Send, Loader, X } from "lucide-react";

interface ReferralEmailFormProps {
  emails: string[];
  inputValue: string;
  isSending: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onRemoveEmail: (email: string) => void;
  onSendInvitations: () => void;
}

const EmailChipInput: React.FC<{
  emails: string[];
  inputValue: string;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onRemoveEmail: (email: string) => void;
}> = ({ emails, inputValue, onInputChange, onKeyDown, onRemoveEmail }) => (
  <div className="space-y-2">
    <Field>
      <FieldContent>
        <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[42px] focus-within:ring-1 focus-within:ring-ring bg-white">
          {emails.map((email) => (
            <div
              key={email}
              className="bg-neutral-700 text-neutral-100 text-xs px-3 py-1 rounded-full flex items-center gap-1"
            >
              {email}
              <button onClick={() => onRemoveEmail(email)} className="hover:text-danger-200">
                <X size={12} />
              </button>
            </div>
          ))}
          <Input
            className="flex-1 border-none focus-visible:ring-0 outline-none text-xs min-w-[200px] bg-transparent h-auto p-0 shadow-none"
            placeholder={
              emails.length === 0 ? "Enter Email Addresses and press Enter or Comma" : ""
            }
            value={inputValue}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            disabled={emails.length >= 5}
          />
        </div>
      </FieldContent>
    </Field>
    {emails.length === 0 && inputValue.length > 0 && (
      <p className="text-xs text-danger-200">Press Enter or Comma to add this email address.</p>
    )}
  </div>
);

const ReferralEmailForm: React.FC<ReferralEmailFormProps> = ({
  emails,
  inputValue,
  isSending,
  onInputChange,
  onKeyDown,
  onRemoveEmail,
  onSendInvitations,
}) => (
  <TabsContent value="email" className="mt-4 space-y-4">
    <div className="text-center">
      <h3 className="text-sm font-semibold mb-1">Send Email Invitations</h3>
      <p className="text-xs text-neutral-400">
        Input an email then <span className="font-bold text-neutral-200">Press ENTER</span> or{" "}
        <span className="font-bold text-neutral-200">Comma</span>. You can add up to 5 email
        addresses at once.
      </p>
    </div>

    <EmailChipInput
      emails={emails}
      inputValue={inputValue}
      onInputChange={onInputChange}
      onKeyDown={onKeyDown}
      onRemoveEmail={onRemoveEmail}
    />

    <Alert variant="info" className="p-3">
      <Info className="h-4 w-4" />
      <AlertDescription className="text-xs ml-2">
        <span className="font-bold">Note:</span> You cannot refer team members or yourself. By
        participating in this program, you agree to our{" "}
        <a href="#" className="underline">
          affiliate terms and conditions
        </a>
        .
      </AlertDescription>
    </Alert>

    <div className="flex items-center justify-between mt-4">
      <span className="text-xs text-neutral-400">{emails.length}/5 email addresses added</span>
      <Button
        onClick={onSendInvitations}
        className="bg-primary-300 hover:bg-primary-300-pressed text-neutral-white h-8 text-xs"
        disabled={isSending}
      >
        {isSending ? (
          <>
            <Loader className="mr-2 h-3 w-3 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="mr-2 h-3 w-3" />
            Send Invitation
          </>
        )}
      </Button>
    </div>
  </TabsContent>
);

export default ReferralEmailForm;
